/**
 * Templates service — types mirroring the precedents-management backend
 * (FE technical spec v1.1, post-Phase-4b drift sweep).
 *
 * Single entry shape: `DocumentTemplateEntry` is what every list endpoint
 * returns *and* what every lifecycle mutation returns in its 200 body. There
 * is no separate detail DTO and no `GET /:id` endpoint — the FE composes the
 * detail drawer from the cached list entry plus on-demand calls (preview /
 * download / versions / history).
 *
 * Virtual Vale-standard entries (`origin: 'system'`) carry `id: null` and
 * a `systemKey` instead — that key is the path component for the
 * `/templates/documents/system/{systemKey}/*` endpoints.
 */

export type DocumentType =
  | 'declaration'
  | 'costs-agreement'
  /**
   * The costs agreement a firm offers a **new enquiry**, before a matter exists.
   * A separate type from `costs-agreement` rather than a second standard under
   * it: the matter-stage agreement's Vale standard recites the executors, the
   * estate and the deceased, none of which resolve before a grant, so the two
   * cannot share a template. Its merge fields are the `lead.*` namespace plus
   * firm, lawyer, date and the signing region.
   *
   * Experimental, so it only appears in the library for firms holding
   * `experimental-precedent-types` — but the enquiry-stage cost-agreement picker
   * offers it regardless, because that surface has its own `leads` gate.
   */
  | 'enquiry-costs-agreement'
  | 'engagement-letter'
  | 'da-cover-letter'
  /**
   * The distribution advice itself — the itemised statement the estate's
   * executors sign to approve a distribution, as opposed to `da-cover-letter`,
   * which is the unsigned letter accompanying a beneficiary's copy. The one Vale
   * type signed by more than one person (LF-6270); experimental, so it only
   * appears for firms holding `experimental-precedent-types`.
   */
  | 'distribution-advice'
  /**
   * The estate's cumulative "as at" account of receipts, payments and what is
   * held in trust — the document a firm issues under the executor's duty to
   * account. **Not** the `distribution-advice`, which is the *authorisation* to
   * pay and is signed; a statement is the *accounting* and is unsigned.
   *
   * Its template is almost entirely dynamic tables (ten sections, each
   * appearing only when the estate has facts in it), so a firm forking it is
   * editing repeating regions rather than prose. Experimental, so it only
   * appears for firms holding `experimental-precedent-types`, and the per-firm
   * `distribution-statement` flag gates the statement surfaces on top.
   */
  | 'distribution-statement'
  /**
   * The firm's **letterhead precedent for institution letters** — the
   * notification-of-death and collection-request letters Vale sends to banks,
   * share registries and super funds.
   *
   * The one type whose *body* Vale writes. Everywhere else the firm authors the
   * document and Vale merges data into it; here the firm authors only the
   * stationery — page size, margins, letterhead branding, first-page and
   * continuation headers/footers, its own page numbering — and Vale injects the
   * whole letter through a single `<<letterBody>>` tag. So the drawer's
   * merge-field panel shows essentially one field, and that is correct rather
   * than a loading failure.
   *
   * Visible to every firm, and a firm that uploads a precedent has its letters
   * rendered from it. Both halves used to be gated — `docx-letterhead` offered
   * the artefact and `institution-letter-precedent` rendered it — until VALE-867
   * retired the pair.
   */
  | 'institution-letter'
  /**
   * The estate-planning precedents ([EPLAN-30]), offered to a firm holding
   * `estate-planning-suite` (the backend's per-type `featureFlag`). They were
   * missing from this union entirely while they sat behind the experimental
   * gate, so a firm that could see them got raw slugs for section headings.
   * {@link ESTATE_PLANNING_DOCUMENT_TYPES} is the runtime list.
   */
  | 'will'
  | 'vic-epoa'
  | 'mtdm-appointment'
  | 'ep-cover-letter'
  | 'letter-of-advice'
  | 'prior-documents-authority'
  | 'executor-remuneration-consent'
  | 'will-schedule'
  | 'codicil'
  | 'mutual-wills-agreement'
  | 'statement-of-wishes'
  | 'letter-of-reasons'
  | 'affidavit-of-testamentary-explanation'
  | 'smsf-binding-nomination'
  | 'corporate-power-of-attorney'
  | 'vic-advance-care-directive'
  | 'qld-advance-health-directive'
  | 'ep-options-letter'
  | 'will-explainer-letter'
  | 'clause-bank'
  /**
   * The Queensland court forms (QCF-2): the court's prescribed forms for a grant
   * of probate, offered to a firm holding `qld-court-forms` (the backend's
   * per-type `featureFlag`). {@link QLD_COURT_FORM_DOCUMENT_TYPES} is the runtime
   * list.
   */
  | 'qld-form-101'
  | 'qld-form-104'
  | 'qld-form-105'
  /**
   * The firm's own free-form precedents — one shared, untyped bucket rather than
   * a Vale-defined document shape. Unlike the five above it has **no Vale
   * standard** (so the list never carries an `origin: 'system'` entry for it) and
   * **no default concept** (see `DOCUMENT_TYPE_SUPPORTS_DEFAULT`).
   */
  | 'custom';

/**
 * Every estate-planning document type, in the library's order: the package's own
 * signing order, then the clause bank that supplies the will's wording. Mirrors
 * the backend's `EP_DOCUMENT_TYPES` plus the clause bank, which are exactly the
 * types gated on `estate-planning-suite`.
 */
export const ESTATE_PLANNING_DOCUMENT_TYPES = [
  'prior-documents-authority',
  'executor-remuneration-consent',
  'will',
  'will-schedule',
  'codicil',
  'mutual-wills-agreement',
  'statement-of-wishes',
  'letter-of-reasons',
  'affidavit-of-testamentary-explanation',
  'smsf-binding-nomination',
  'vic-epoa',
  'corporate-power-of-attorney',
  'mtdm-appointment',
  'vic-advance-care-directive',
  'qld-advance-health-directive',
  'ep-options-letter',
  'ep-cover-letter',
  'letter-of-advice',
  'will-explainer-letter',
  'clause-bank'
] as const satisfies readonly DocumentType[];

/** The Queensland court forms, in form order. Mirrors the backend's `QLD_COURT_FORM_DOCUMENT_TYPES`. */
export const QLD_COURT_FORM_DOCUMENT_TYPES = [
  'qld-form-101',
  'qld-form-104',
  'qld-form-105'
] as const satisfies readonly DocumentType[];

export type TemplateOrigin = 'firm' | 'system';

/**
 * What happens to a firm's fork when Vale ships a new version of the starter
 * (VALE-1580). `firm-owned` (every type today): Vale never touches the fork.
 * `tracks-system`: the fork follows Vale's revisions automatically, its previous
 * content kept in History — used for court forms, which no firm may drift from.
 * Read straight from the type definition; a type with no Vale starter (custom)
 * reports `firm-owned`.
 */
export type SystemTemplateForkPolicy = 'firm-owned' | 'tracks-system';

/**
 * "Edit in Word" (M365 / SharePoint Embedded) checkout state of a firm
 * template. `null` (or absent) = not checked out. Mirrors the backend
 * `TemplateCheckoutState` enum — a single non-null value in v1; the soft-lock
 * ("one editor at a time") keys on the presence of `checked_out`. Virtual
 * Vale-standard entries are never checked out.
 */
export type TemplateCheckoutState = 'checked_out';

export interface TemplateActorUser {
  id: number;
  fullName: string;
}

export interface DocumentTemplateEntry {
  /** Null for virtual Vale-standard entries. */
  id: number | null;
  /** Present iff `origin === 'system'` — the stable identifier the BE uses
   *  in `/templates/documents/system/{systemKey}/*` endpoints. */
  systemKey?: string;
  origin: TemplateOrigin;
  documentType: DocumentType;
  name: string;
  /** Firm rows: the row-level default flag. Virtual system entries: always
   *  false. The FE computes the *effective* default for each documentType
   *  (firm row with `isDefault: true` if one exists, else the system entry). */
  isDefault: boolean;
  /** The office this precedent belongs to (VALE-540), or null for the
   *  firm-level one. The branding page's letterhead list groups rows by it. */
  officeId: number | null;
  /** True for the Phase 2 document types (costs-agreement, engagement-letter,
   *  da-cover-letter). Only surfaces as true for firms with the
   *  `experimental-precedent-types` flag on (BE filters experimental rows
   *  out of the list otherwise). Drives the `Experimental` section badge. */
  isExperimental: boolean;
  /** Firm rows: the template's currentVersion. Virtual entries: the system
   *  template's `version` constant. */
  currentVersion: number;
  /** The system template the firm forked from at commit time. Surfaces in
   *  the `created` history event metadata. */
  forkedFromSystemKey: string | null;
  /** Convenience: `true` iff `origin === 'firm' && forkedFromSystemKey !=
   *  null`. Derivable but surfaced by the BE for free. */
  customised: boolean;
  /** What happens to a firm's fork of this type when Vale revises the starter
   *  (VALE-1580). `tracks-system` drives the "Vale checks for updates" marker;
   *  every type is `firm-owned` today. Present on both firm and system entries. */
  forkPolicy: SystemTemplateForkPolicy;
  archived: boolean;
  /** ISO 8601 when archived; null otherwise. */
  archivedAt: string | null;
  /** ISO 8601. Null for virtual system entries. */
  updatedAt: string | null;
  /** Undefined on virtual system entries. The user who created or last
   *  edited the firm row. */
  createdByUser?: TemplateActorUser;

  // ── "Edit in Word" (M365 / SPE) — LF-5224 -------------------------------
  // All optional: the backend always sends them (BE-11 + BE-12), but they're
  // absent on locally-constructed literals (mocks/tests), so the FE reads
  // them defensively. Always `null`/`false` on virtual system entries and for
  // firms without the feature.

  /** `checked_out` while a working copy is open in Word (BE-11); `null` when
   *  not checked out. Drives the "Editing in Word" pill + soft-lock. */
  checkoutState?: TemplateCheckoutState | null;
  /** The user currently holding the "Edit in Word" checkout — powers the
   *  "Being edited by {user}" soft-lock display. Set iff `checkoutState` is
   *  `checked_out`. */
  checkedOutByUser?: TemplateActorUser | null;
  /** Firm-level: `true` iff the firm has active M365/SPE consent. The single
   *  gate for the "Edit in Word" CTA — `false` (no consent) means
   *  byte-for-byte today's UX, no CTA, no upsell (SP-5's silent-fallback
   *  contract). Present on every entry. */
  wordEditingAvailable?: boolean;
  /** ISO 8601 — when an edited Word version was last pulled back from SPE and
   *  re-ingested to S3 as a new version (BE-12). Drives the "Synced from Word
   *  N min ago" label. `null` until the first re-ingest of the current
   *  checkout; `null` on virtual system entries. */
  lastSyncedFromWordAt?: string | null;
  /** Merge-field validation result of the most recently re-ingested Word bytes
   *  (BE-12) — the payload behind the non-blocking amber post-sync advisory.
   *  Same shape as the upload/preview warnings. `null` = never synced from
   *  Word; `[]` = synced clean (no advisory); non-empty = advisory. */
  lastSyncWarnings?: UploadPreviewWarning[] | null;
}

// ---------------------------------------------------------------------------
// Merge fields (flat registry shape — FE groups by namespace client-side)
// ---------------------------------------------------------------------------

export type MergeFieldNamespace =
  | 'recipient'
  | 'deceased'
  | 'lpr'
  | 'lawFirm'
  /** The person signing the document. `lawyer` is the retired name for the same
   *  fields — kept in the union because saved templates still carry the tokens,
   *  but the API never lists them (they are `hidden` server-side). */
  | 'signatory'
  | 'lawyer'
  /** The sending office — the matter's on a document, the signer's in a signature. */
  | 'office'
  | 'matter'
  | 'estate'
  /** The pre-matter enquiry — the only namespace that resolves *before* a matter. */
  | 'lead'
  /** The estate-planning perspective namespaces ([EPLAN-6]): the person the document is for, their partner, the family, and the letters' `plan.*`. */
  | 'testator'
  | 'partner'
  | 'family'
  | 'plan'
  /** The Queensland court forms' recitals and exhibit role letters (QCF-2), classified for those forms alone. */
  | 'court'
  | 'exhibit'
  | 'system'
  | 'other';

export interface MergeFieldDefinition {
  /** Bare field name like `beneficiary.fullName`. The token form
   *  (`<<beneficiary.fullName>>`) is derived FE-side. */
  name: string;
  namespace: MergeFieldNamespace;
  description: string;
  exampleValue: string;
  required: boolean;
  isImage: boolean;
}

export interface MergeFieldsRegistry {
  documentType: DocumentType;
  fields: MergeFieldDefinition[];
}

// ---------------------------------------------------------------------------
// Versions / history
// ---------------------------------------------------------------------------

export interface DocumentTemplateVersion {
  id: number;
  versionNumber: number;
  uploadedAt: string;
  uploadedByUserName: string | null;
  commitMessage: string | null;
  isCurrent: boolean;
}

export type DocumentTemplateHistoryEventType =
  | 'created'
  | 'renamed'
  | 'set-as-default'
  | 'unset-as-default'
  | 'archived'
  | 'unarchived'
  // A tracks-system fork was auto-brought-current to a newer Vale version
  // (VALE-1580). Actor is the system, so `actorUserName` is null.
  | 'updated-to-system-version';

/**
 * History metadata shapes — discriminated by the parent event's `eventType`,
 * NOT by an internal `kind` tag (the BE doesn't emit one).
 */
export type DocumentTemplateHistoryMetadata =
  | { source: 'upload' } // eventType === 'created'
  | {
      source: 'fork';
      forkedFromSystemKey: string;
      forkedFromSystemVersion: number;
    } // eventType === 'created'
  | {
      // eventType === 'created' — a matter copy taken from a firm fork that
      // itself tracks a system template (VALE-1580).
      source: 'fork-of-firm-template';
      firmTemplateId: number;
      forkedFromSystemKey: string;
      forkedFromSystemVersion: number;
    }
  | { from: string; to: string } // eventType === 'renamed'
  | { previousDefaultId: number | null } // eventType === 'set-as-default'
  | { displacedByTemplateId: number } // eventType === 'unset-as-default'
  | {
      // eventType === 'updated-to-system-version'
      fromSystemVersion: number | null;
      toSystemVersion: number;
      supersededVersionNumber: number;
    }
  | Record<string, never>; // archived | unarchived

export interface DocumentTemplateHistoryEvent {
  id: number;
  eventType: DocumentTemplateHistoryEventType;
  actorUserName: string | null;
  occurredAt: string;
  metadata: DocumentTemplateHistoryMetadata;
}

// ---------------------------------------------------------------------------
// Upload (three-step) request/response shapes
// ---------------------------------------------------------------------------

/**
 * Warning codes the backend emits (`backend/.../merge-fields/types/validation.types.ts`).
 *
 * `ValidationBlock` gives `unknown-field` and `missing-signature-block` bespoke,
 * grouped presentation and renders every **other** code generically from its own
 * `title` / `description`.
 * That fallback is deliberate: the backend writes each of these as a complete
 * author-facing sentence, and a UI that enumerated codes instead would show the
 * lawyer an empty warning box — with an "I've reviewed these" checkbox above
 * nothing — the moment the backend added one this list had not caught up with.
 */
export type UploadPreviewWarningCode =
  | 'unknown-field'
  // The slot Vale writes a whole document into (`letterBody` on the institution
  // letterhead), absent from the template. NOT a required *data* field — Vale no
  // longer warns about those at upload, because a firm that drafts around a field
  // has authored a correct document (VALE-1671).
  | 'missing-content-region'
  | 'missing-signature-block'
  | 'template-parse-error'
  | 'unknown-section'
  | 'region-misplaced'
  | 'region-unguarded'
  | 'column-outside-region'
  | 'section-tag-used-as-value'
  // Typeface coverage (VALE-884) and embedded-font hazards (VALE-1590). All
  // three render through the generic fallback above — the backend writes
  // each as a complete author-facing sentence.
  | 'font-unsupported'
  | 'font-substituted'
  | 'font-embed-subsetted';

export interface UploadPreviewWarning {
  code: UploadPreviewWarningCode;
  title: string;
  description: string;
  /** Literal tag found in the .docx (e.g. `{{Client/Bank}}`). */
  tag?: string;
  /** Vale-canonical token the engine confidently matches the tag against
   *  (e.g. `<<beneficiary.bankAccountNumber>>`). Absent => "no suggestion". */
  suggestion?: string;
}

export interface UploadPreviewError {
  title: string;
  description: string;
}

export interface UploadPreviewResponse {
  ok: boolean;
  /** Base64-encoded PDF — decoded into a blob URL by the upload drawer.
   *  Null on hard errors (`ok === false`). */
  previewPdfBase64: string | null;
  warnings: UploadPreviewWarning[];
  errors?: UploadPreviewError[];
  /** Scalar merge-field tag names actually used by the template (no `<<`/`>>`
   *  wrappers, loop / inverse / raw-XML markers stripped). The merge-fields
   *  reference panel uses this to filter to fields-in-use with a "Show N
   *  unused" disclosure. */
  tagsInTemplate: string[];
}

export interface CommitTemplateRequest {
  fileId: number;
  name: string;
  documentType: DocumentType;
  setAsDefault?: boolean;
  /** Required iff forking from a Vale standard. */
  forkedFromSystemKey?: string;
  /** Required iff the prior preview returned non-empty `warnings`. */
  warningsAcknowledged?: boolean;
  /** The office this precedent belongs to (VALE-540). Absent is the
   *  firm-level precedent. An office precedent never claims the firm-wide
   *  default, so `setAsDefault` is ignored alongside it. */
  officeId?: number;
}

export interface ReplaceContentRequest {
  fileId: number;
  commitMessage?: string;
  warningsAcknowledged?: boolean;
}

export interface ArchiveTemplateRequest {
  /** Required iff the target is the current default AND other actives exist
   *  for the document type. The backend enforces this and surfaces
   *  `REPLACEMENT_DEFAULT_REQUIRED` (409) when missing. */
  replacementDefaultId?: number;
}

// ---------------------------------------------------------------------------
// Edit in Word (provision-on-edit) — LF-5224 / BE-11
// ---------------------------------------------------------------------------

/**
 * `POST /templates/documents/edit-in-word`. Exactly one form:
 *  - `templateId` — a firm row: opens its working copy directly.
 *  - `systemKey` + `documentType` — a Vale-standard entry: fork-first, then
 *    open the new firm copy's working copy.
 */
export type EditInWordRequest =
  | { templateId: number }
  | { systemKey: string; documentType: DocumentType };

export interface EditInWordResponse {
  /** The firm template now checked out (the post-fork id when `forked`). */
  templateId: number;
  /** Office-for-web edit URL — opened in a new tab (launch-to-new-tab, SP-1). */
  webUrl: string;
  checkoutState: TemplateCheckoutState;
  /** True when this call performed the Vale-standard → firm fork-first step. */
  forked: boolean;
  /** Why the per-user SharePoint grant failed, or null when it landed. See
   *  {@link SpeInviteFailureReason} — a non-null value must be surfaced. */
  inviteFailure: SpeInviteFailureReason | null;
}

/**
 * Why a per-user SharePoint access grant did not land on a Word working copy.
 * Shared by every Edit-in-Word surface (templates, declarations, generated
 * attachments, lead cost agreements, distribution statements) — they all check
 * out through the one backend `EditableDocumentService`, so they all report the
 * same reasons.
 *
 * - `identity-not-in-tenant` — the Vale account email is not a member of the
 *   firm's Microsoft 365 tenant, so there is no identity to grant. Deterministic:
 *   retrying grants nothing.
 * - `unknown` — a transient failure; a retry (every reopen re-invites) may work.
 */
export type SpeInviteFailureReason = 'identity-not-in-tenant' | 'unknown';

/**
 * What an on-demand "pull my Word changes now" actually did.
 *
 * Shared by every surface that offers the manual refresh — the composer's
 * generated attachment, the issued distribution statement, the lead cost
 * agreement — because they all run the one backend read seam and must report it
 * in the same words (VALE-288).
 *
 * The call never fails loudly: the seam is an accelerator over the ~30s change
 * notification, so a Graph hiccup must not surface as a broken action. But
 * `failed` and `up-to-date` must not look the same to the lawyer. Both are
 * silent, and silence after pressing refresh reads as "Vale has my latest"
 * (VALE-258). The automatic focus pull ignores this; the manual control says so.
 */
export type RefreshFromWordStatus =
  /** No Word session, so nothing to check. Zero Graph calls. */
  | 'not-checked-out'
  /** Word had a newer save and it is now what Vale holds. */
  | 'ingested'
  /** Word was reached and had nothing newer. */
  | 'up-to-date'
  /** Word could not be reached. What is on screen may not be the latest save. */
  | 'failed';

export interface RefreshFromWordResult {
  status: RefreshFromWordStatus;
}

/**
 * The advisory shown when the document opened but the per-user grant did not
 * land. A pure mapping, for the same reason `editInWordErrorMessage` is one: the
 * distinction it encodes is load-bearing, and it is the whole fix for VALE-115.
 *
 * The failure is **non-fatal** — `webUrl` is a real working copy — so this is a
 * warning, never an error: without it the user simply meets Microsoft's bare
 * "You can't access this item" page and is left to guess. `identity-not-in-tenant`
 * must not invite a retry (the same trap `TENANT_NOT_ENABLED` exists to avoid),
 * and both branches name the download fallback, which always works.
 */
export function wordInviteFailureToast(reason: SpeInviteFailureReason): {
  title: string;
  description: string;
} {
  return reason === 'identity-not-in-tenant'
    ? {
        title: 'Opened, but Word may refuse access',
        description:
          "Your Vale account email isn't a member of your firm's Microsoft 365 tenant, so we couldn't grant you access to the working copy. Download the document and edit it locally. Trying again won't help."
      }
    : {
        title: 'Opened, but Word may refuse access',
        description:
          "We couldn't grant you access to the working copy. If Word shows an access error, try again in a moment, or download the document and edit it locally."
      };
}

/**
 * Coded errors the provision-on-edit endpoint returns (switch on
 * `DirectApiError.response.code`, never the message). `SPE_NOT_AVAILABLE`
 * (no active M365 consent) means "no Edit in Word, fall back silently" on the
 * FE — the CTA is already gated on `wordEditingAvailable`, so this is a
 * defensive backstop.
 */
export const EDIT_IN_WORD_ERROR = {
  SPE_NOT_AVAILABLE: 'WORD_EDITING_SPE_NOT_AVAILABLE',
  // No TEMPLATE_ALREADY_CHECKED_OUT: a second edit-in-word reopens the working
  // copy idempotently rather than erroring (LF-5363 — soft-lock removed).
  PROVISIONING_FAILED: 'WORD_EDITING_PROVISIONING_FAILED',
  /**
   * The firm's M365 tenant is consented but not fully enabled, so a working copy
   * can never be created (LF-5943). Deterministic — distinct from
   * `PROVISIONING_FAILED` precisely so the toast does not say "try again", which
   * is what kept the O'Neill Solicitors onboarding retrying a setup gap.
   */
  TENANT_NOT_ENABLED: 'WORD_EDITING_TENANT_NOT_ENABLED'
} as const;

export type EditInWordErrorCode =
  (typeof EDIT_IN_WORD_ERROR)[keyof typeof EDIT_IN_WORD_ERROR];

/**
 * The toast copy for a failed "Edit in Word", by coded error. A pure mapping so
 * the choice is unit-testable — the distinction it encodes is load-bearing:
 * `TENANT_NOT_ENABLED` is deterministic and must NOT invite a retry, whereas
 * `PROVISIONING_FAILED` is the known ~25% SPE container-create flake and must.
 * Getting that backwards is what left the O'Neill Solicitors onboarding
 * retrying a setup gap four times (LF-5943).
 *
 * An unrecognised or absent code falls back to the availability wording, which
 * is also the right message for `SPE_NOT_AVAILABLE` ("no Edit in Word here" —
 * a defensive backstop, since the CTA is gated on `wordEditingAvailable`).
 */
export function editInWordErrorMessage(code: string | undefined): string {
  switch (code) {
    case EDIT_IN_WORD_ERROR.TENANT_NOT_ENABLED:
      // Names the action that fixes it, now that it exists. This used to say
      // "contact support", because the only route back to Microsoft consent for
      // a live firm was to turn the whole connection off first — so support
      // walked the admin through the Microsoft portal by hand (VALE-214).
      return "Your firm's Microsoft 365 setup isn't finished, so documents can't be opened in Word yet. Your firm administrator can fix it from Account → Integrations by re-authorising Microsoft 365. Retrying won't help. You can download and re-upload in the meantime.";
    case EDIT_IN_WORD_ERROR.PROVISIONING_FAILED:
      return "We couldn't open this template in Word. Try again, or use download / upload instead.";
    default:
      return "Editing in Word isn't available right now. You can download or upload a new version instead.";
  }
}
